import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { api } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import Icon from '../components/Icon';
import './AnalyseTemperatures.css';

const PRODUITS = [
  { value: 'porc',         label: 'Porc Effiloché' },
  { value: 'brisket',      label: 'Brisket' },
  { value: 'saucisse',     label: 'Saucisses' },
  { value: 'saumon',       label: 'Saumon Fumé' },
  { value: 'cotes_levees', label: 'Côtes Levées' },
];

export default function AnalyseTemperatures() {
  const [produit, setProduit]         = useState('porc');
  const [depuis, setDepuis]           = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [jusqu, setJusqu]             = useState(() => new Date().toISOString().slice(0, 10));
  const [donnees, setDonnees]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [erreur, setErreur]           = useState('');
  const [batchRef, setBatchRef]       = useState(null);
  const [highlightBatch, setHighlightBatch] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur('');
    try {
      const params = new URLSearchParams({ produit, depuis, jusqu });
      const data = await api.get(`/meater/analyse/moyenne?${params}`);
      setDonnees(data);
      setBatchRef(null);
    } catch (e) {
      setErreur(e?.response?.data?.error || e.message || 'Erreur de chargement');
      setDonnees(null);
    } finally {
      setLoading(false);
    }
  }, [produit, depuis, jusqu]);

  useEffect(() => {
    charger();
  }, []); // eslint-disable-line

  const buckets   = donnees?.buckets  || [];
  const batches   = donnees?.batches  || [];
  const nbBatches = donnees?.nb_batches ?? 0;

  const COULEUR_INDIVIDUELLE = 'rgba(138,131,112,0.35)';

  const dataGraphe = buckets.map(b => ({
    t_min       : b.t_min,
    interne_moy : b.interne_moy != null ? +b.interne_moy.toFixed(1) : null,
    interne_sup : b.interne_moy != null && b.interne_ecart != null
      ? +(b.interne_moy + b.interne_ecart).toFixed(1) : null,
    interne_inf : b.interne_moy != null && b.interne_ecart != null
      ? +Math.max(0, b.interne_moy - b.interne_ecart).toFixed(1) : null,
    ambiante_moy: b.ambiante_moy != null ? +b.ambiante_moy.toFixed(1) : null,
  }));

  function genererInsights() {
    if (!donnees || nbBatches === 0) return [];
    const insights = [];

    if (donnees.duree_moyenne_min) {
      const h = Math.floor(donnees.duree_moyenne_min / 60);
      const m = donnees.duree_moyenne_min % 60;
      insights.push(`Durée moyenne de cuisson : ${h}h${String(m).padStart(2, '0')}`);
    }
    if (donnees.temp_max_moy) {
      insights.push(`Température interne maximale moyenne : ${donnees.temp_max_moy.toFixed(1)}°F`);
    }
    if (donnees.ecart_fumoir_moyen != null) {
      const stable = donnees.ecart_fumoir_moyen <= 10;
      insights.push(
        `Stabilité fumoir : écart-type moyen ${donnees.ecart_fumoir_moyen.toFixed(1)}°F (${stable ? 'stable' : 'instable'})`
      );
    }
    const batchesAnomalie = batches.filter(b => b.nb_anomalies > 0);
    if (batchesAnomalie.length > 0) {
      insights.push(
        `${batchesAnomalie.length} batch(es) avec anomalies : ${batchesAnomalie.map(b => b.nom || `#${b.id}`).join(', ')}`
      );
    }
    return insights;
  }

  const insights = genererInsights();

  return (
    <div className="analyse-temperatures rise-in">

      {/* ── En-tête ───────────────────────────────────────────────────── */}
      <div className="at-page-header">
        <h1 className="at-page-title">Analyse comparative des cuissons</h1>
        <p className="at-page-subtitle">
          Superposition des courbes de température par produit — détection des patterns et anomalies
        </p>
      </div>

      {/* ── Filtres ───────────────────────────────────────────────────── */}
      <div className="card at-filtres">
        <div className="at-filtres-grid">
          <div className="at-filtre-champ">
            <label>Produit</label>
            <select value={produit} onChange={e => setProduit(e.target.value)}>
              {PRODUITS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="at-filtre-champ">
            <label>Depuis</label>
            <DateInput value={depuis} onChange={setDepuis} />
          </div>
          <div className="at-filtre-champ">
            <label>Jusqu'au</label>
            <DateInput value={jusqu} onChange={setJusqu} />
          </div>
          {batches.length > 0 && (
            <div className="at-filtre-champ">
              <label>Batch référence</label>
              <select value={batchRef ?? ''} onChange={e => setBatchRef(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Aucun</option>
                {batches.map(b => (
                  <option key={b.id} value={b.id}>{b.nom || `Batch #${b.id}`}</option>
                ))}
              </select>
            </div>
          )}
          <div className="at-filtre-champ" style={{ alignSelf: 'flex-end' }}>
            <button className="btn btn-primary" onClick={charger} disabled={loading}>
              <Icon name="recherche" size={14} />
              {loading ? 'Chargement…' : 'Analyser'}
            </button>
          </div>
        </div>
        {nbBatches > 0 && (
          <p className="at-filtres-n">N = {nbBatches} batch(es) dans la période</p>
        )}
      </div>

      {/* ── Erreur ────────────────────────────────────────────────────── */}
      {erreur && (
        <div className="error-inline">
          <Icon name="erreur" size={16} />
          {erreur}
        </div>
      )}

      {/* ── Loading ───────────────────────────────────────────────────── */}
      {loading && (
        <div className="skeleton" style={{ height: 380, borderRadius: 'var(--radius-lg)' }} />
      )}

      {/* ── Vide ──────────────────────────────────────────────────────── */}
      {!loading && donnees && nbBatches === 0 && (
        <div className="card at-vide">
          <div className="empty-state">
            <Icon name="courbe" size={36} />
            <div className="empty-state__titre">Aucune donnée de température</div>
            <span>
              Aucun batch avec données de température pour cette période.
              Assurez-vous d'avoir des sessions MEATER ou des fichiers CSV importés pour ce produit.
            </span>
          </div>
        </div>
      )}

      {/* ── Graphique ─────────────────────────────────────────────────── */}
      {!loading && dataGraphe.length > 0 && (
        <div className="card at-graphe-card">
          <div className="at-graphe-header">
            <h3 className="at-graphe-titre">
              Superposition des courbes — {PRODUITS.find(p => p.value === produit)?.label}
            </h3>
          </div>

          <div className="at-legende-manuelle">
            <span>
              <span className="at-leg-dot" style={{ background: 'var(--accent)' }} />
              Moyenne interne
            </span>
            <span>
              <span className="at-leg-area" style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)' }} />
              Zone ±1 écart-type
            </span>
            <span>
              <span className="at-leg-dot" style={{ background: COULEUR_INDIVIDUELLE, opacity: 1 }} />
              Batches individuels
            </span>
            {batchRef && (
              <span>
                <span className="at-leg-dot" style={{ background: 'var(--danger)' }} />
                Batch référence
              </span>
            )}
          </div>

          <div style={{ height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="t_min"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={v => `${v}min`}
                  stroke="var(--muted)"
                  tick={{ fontSize: 11 }}
                  label={{ value: 'Temps (min)', position: 'insideBottom', offset: -10, fill: 'var(--muted)', fontSize: 11 }}
                  allowDuplicatedCategory={false}
                />
                <YAxis
                  stroke="var(--muted)"
                  tick={{ fontSize: 11 }}
                  label={{ value: '°F', angle: -90, position: 'insideLeft', fill: 'var(--muted)', fontSize: 11 }}
                  domain={[0, 'dataMax + 20']}
                />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', fontSize: 12 }}
                  labelFormatter={v => `${v} min`}
                  formatter={(val, name) => val != null ? [`${typeof val === 'number' ? val.toFixed(1) : val}°F`, name] : ['—', name]}
                />

                <Area
                  data={dataGraphe}
                  dataKey="interne_sup"
                  name="Zone haute"
                  fill="rgba(200,148,26,0.18)"
                  stroke="none"
                  legendType="none"
                  dot={false}
                  type="monotone"
                  connectNulls
                  isAnimationActive={false}
                />
                <Area
                  data={dataGraphe}
                  dataKey="interne_inf"
                  name="Zone basse"
                  fill="var(--bg)"
                  stroke="none"
                  legendType="none"
                  dot={false}
                  type="monotone"
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  data={dataGraphe}
                  dataKey="interne_moy"
                  name="Moy. interne"
                  stroke="var(--accent)"
                  strokeWidth={3}
                  dot={false}
                  type="monotone"
                  connectNulls
                  isAnimationActive={false}
                />

                {batches.map(b => {
                  const isRef = b.id === batchRef;
                  const isHL  = b.id === highlightBatch;
                  const pts   = (b.points || []).map(p => ({
                    t_min: p.t_min,
                    [`interne_${b.id}`]: p.temp_interne_f ?? null,
                  }));
                  if (pts.length === 0) return null;
                  return (
                    <Line
                      key={b.id}
                      data={pts}
                      dataKey={`interne_${b.id}`}
                      name={b.nom || `Batch #${b.id}`}
                      stroke={isRef ? 'var(--danger)' : isHL ? 'var(--info)' : COULEUR_INDIVIDUELLE}
                      strokeWidth={isRef ? 3 : isHL ? 2 : 1}
                      dot={false}
                      type="monotone"
                      connectNulls
                      legendType="none"
                      isAnimationActive={false}
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Insights ──────────────────────────────────────────────────── */}
      {insights.length > 0 && (
        <div className="card at-insights">
          <div className="at-insights-header">
            <span className="at-insights-icon"><Icon name="idee" size={16} /></span>
            <span className="at-insights-titre">Insights automatiques</span>
          </div>
          <ul className="at-insights-list">
            {insights.map((txt, i) => <li key={i}>{txt}</li>)}
          </ul>
        </div>
      )}

      {/* ── Liste batches ─────────────────────────────────────────────── */}
      {batches.length > 0 && (
        <div className="card">
          <div className="at-batches-header">
            <Icon name="liste" size={15} color="var(--muted)" />
            <span className="at-batches-title">Batches individuels</span>
            <span className="at-batches-count">{batches.length} batch(es)</span>
          </div>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Batch</th>
                <th>Date</th>
                <th>Durée</th>
                <th>Temp max</th>
                <th>Anomalies</th>
                <th>Référence</th>
              </tr>
            </thead>
            <tbody>
              {batches.map(b => (
                <tr
                  key={b.id}
                  style={{ cursor: 'pointer' }}
                  className={highlightBatch === b.id ? 'at-row-hl' : undefined}
                  onClick={() => setHighlightBatch(prev => prev === b.id ? null : b.id)}
                >
                  <td style={{ fontWeight: 600 }}>{b.nom || `Batch #${b.id}`}</td>
                  <td>{b.date ? b.date.slice(0, 10) : '—'}</td>
                  <td>
                    {b.duree_min
                      ? `${Math.floor(b.duree_min / 60)}h${String(b.duree_min % 60).padStart(2, '0')}`
                      : '—'}
                  </td>
                  <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>
                    {b.temp_max_f != null ? `${b.temp_max_f.toFixed(1)}°F` : '—'}
                  </td>
                  <td>
                    {b.nb_anomalies > 0
                      ? <span className="badge badge--danger"><Icon name="alerte" size={11} />{b.nb_anomalies} anomalie(s)</span>
                      : <span className="badge badge--success"><Icon name="succes" size={11} />Aucune</span>}
                  </td>
                  <td>
                    <button
                      className={`btn btn--sm${batchRef === b.id ? ' btn-primary' : ' btn-ghost'}`}
                      onClick={e => { e.stopPropagation(); setBatchRef(prev => prev === b.id ? null : b.id); }}
                    >
                      {batchRef === b.id ? 'Désépingler' : 'Épingler'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="at-batches-hint">Cliquer sur une ligne pour surligner la courbe dans le graphique.</p>
        </div>
      )}
    </div>
  );
}
